import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../config/api';
import { getUserData } from '../../utils/auth';
import { ADMIN_ALLOWED_USERS } from '../../utils/userUtils';
import MassSMSHistory from './MassSMSHistory';
import './styles/CustomerSearch.css';

const RECENT_KEY = 'recentCustomers';
const RECENT_MAX = 8;

// ─── helpers ────────────────────────────────────────────────────────────────

const loadRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; }
  catch { return []; }
};

const saveRecent = (customer) => {
  const prev = loadRecent().filter(c => c.ID !== customer.ID);
  const next = [customer, ...prev].slice(0, RECENT_MAX);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
};

const normName = (str) =>
  (str || '').toLowerCase().replace(/[^a-z0-9]/g, '').trim();

const normPhone = (str) =>
  (str || '').replace(/\D/g, '').replace(/^1/, '');

const phonesNearMatch = (a, b) => {
  const na = normPhone(a);
  const nb = normPhone(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.length === nb.length + 1) return na.slice(1) === nb || na.slice(0, -1) === nb;
  if (nb.length === na.length + 1) return nb.slice(1) === na || nb.slice(0, -1) === na;
  return false;
};

const isDuplicatePair = (a, b) => {
  if (a.ID === b.ID) return false;
  const nameMatch =
    normName(a.CustomerFullName || `${a.CustomerFirstName} ${a.CustomerLastName}`) ===
    normName(b.CustomerFullName || `${b.CustomerFirstName} ${b.CustomerLastName}`);
  return nameMatch || phonesNearMatch(a.CustomerPhone, b.CustomerPhone);
};

const buildDuplicateSet = (list) => {
  const ids = new Set();
  for (let i = 0; i < list.length; i++)
    for (let j = i + 1; j < list.length; j++)
      if (isDuplicatePair(list[i], list[j])) { ids.add(list[i].ID); ids.add(list[j].ID); }
  return ids;
};

const formatPhone = (phone) => {
  if (!phone) return 'N/A';
  const c = phone.replace(/\D/g, '');
  if (c.length === 10) return `(${c.slice(0, 3)}) ${c.slice(3, 6)}-${c.slice(6)}`;
  return phone;
};

const highlightMatch = (text, term) => {
  if (!text || !term) return text;
  const re = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  return text.split(re).map((part, i) =>
    re.test(part) ? <mark key={i} className="search-highlight">{part}</mark> : part
  );
};

const customerName = (c) =>
  c.CustomerFullName || `${c.CustomerFirstName || ''} ${c.CustomerLastName || ''}`.trim();

// ─── component ──────────────────────────────────────────────────────────────

const CustomerSearch = () => {
  const navigate = useNavigate();

  // ── main search ──
  const [searchTerm, setSearchTerm]     = useState('');
  const [customers, setCustomers]       = useState([]);
  const [isLoading, setIsLoading]       = useState(false);
  const [error, setError]               = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [recentlyViewed, setRecentlyViewed] = useState(loadRecent);
  const [duplicateIds, setDuplicateIds] = useState(new Set());
  const [deletingId, setDeletingId] = useState(null);

  // ── admin check ──
  const isAdmin = ADMIN_ALLOWED_USERS.includes(
    getUserData()?.UserName?.toLowerCase()
  );

  // ── mass text modal ──
  const [showHistory, setShowHistory]       = useState(false);
  const [showMassModal, setShowMassModal]   = useState(false);
  const [massMessage, setMassMessage]       = useState('');
  const [massImage, setMassImage]           = useState(null);
  const [massImagePreview, setMassImagePreview] = useState(null);
  const [isSending, setIsSending]           = useState(false);
  const [sendResult, setSendResult]         = useState(null);
  const [jobId, setJobId]                   = useState(null);
  const [jobProgress, setJobProgress]       = useState(null);
  const pollRef                             = useRef(null);
  const isPollingFetchRef                   = useRef(false); // prevents overlapping status polls
  const massImageInputRef                   = useRef(null);

  // send-to-all mode
  const [sendToAll, setSendToAll]           = useState(false);
  const [allCount, setAllCount]             = useState(null);
  const [fetchingCount, setFetchingCount]   = useState(false);

  // modal search
  const [modalSearchTerm, setModalSearchTerm]       = useState('');
  const [modalSearchResults, setModalSearchResults] = useState([]);
  const [modalSearchLoading, setModalSearchLoading] = useState(false);

  // modal location filter
  const [locFilter, setLocFilter]     = useState({ state: '', city: '' });
  const [locCustomers, setLocCustomers] = useState([]);
  const [loadingLoc, setLoadingLoc]   = useState(false);

  // selected recipients: Map<ID, customer> so we keep the name/phone even after list changes
  const [selectedRecipients, setSelectedRecipients] = useState(new Map());

  const rowRefs  = useRef([]);
  const inputRef = useRef(null);
  const modalInputRef = useRef(null);


  // ── check for active mass SMS job on mount ──────────────────────────────

  useEffect(() => {
    if (!isAdmin) return;
    let interval = null;

    axios.get(`${API_URL}/api/customers/mass-sms/active`).then(({ data }) => {
      if (!data.job) return;
      const j = data.job;
      setJobId(j.jobId);
      setJobProgress(j);

      if (['running', 'queued', 'resuming'].includes(j.status)) {
        setIsSending(true);
        interval = setInterval(async () => {
          if (isPollingFetchRef.current) return;
          isPollingFetchRef.current = true;
          try {
            const { data: s } = await axios.get(`${API_URL}/api/customers/mass-sms/status/${j.jobId}`);
            setJobProgress(s);
            if (['completed', 'cancelled', 'failed'].includes(s.status)) {
              clearInterval(interval);
              pollRef.current = null;
              setIsSending(false);
              setSendResult(s);
            }
          } catch { /* ignore poll errors */ }
          finally { isPollingFetchRef.current = false; }
        }, 2000);
        pollRef.current = interval;
      }
    }).catch(() => { /* ignore — user may not have permission */ });

    return () => { if (interval) clearInterval(interval); };
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── main search ──────────────────────────────────────────────────────────

  useEffect(() => {
    const id = setTimeout(() => {
      if (searchTerm.length >= 2) {
        doSearch(searchTerm);
      } else {
        setCustomers([]);
        setDuplicateIds(new Set());
        setFocusedIndex(-1);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  const doSearch = async (term) => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/customers/search`, { params: { term } });
      const list = data || [];
      setCustomers(list);
      setDuplicateIds(buildDuplicateSet(list));
      setFocusedIndex(-1);
    } catch {
      setError('Failed to search customers');
      setCustomers([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ── keyboard nav ─────────────────────────────────────────────────────────

  const handleKeyDown = useCallback((e) => {
    if (!customers.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(i => { const n = Math.min(i + 1, customers.length - 1); rowRefs.current[n]?.scrollIntoView({ block: 'nearest' }); return n; });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(i => { const n = Math.max(i - 1, 0); rowRefs.current[n]?.scrollIntoView({ block: 'nearest' }); return n; });
    } else if (e.key === 'Enter') {
      if (focusedIndex >= 0 && customers[focusedIndex]) handleCustomerClick(customers[focusedIndex]);
    } else if (e.key === 'Escape') {
      setSearchTerm(''); inputRef.current?.focus();
    }
  }, [customers, focusedIndex]);

  // ── navigation & recent ──────────────────────────────────────────────────

  const handleCustomerClick = (customer) => {
    saveRecent(customer);
    setRecentlyViewed(loadRecent());
    navigate(`/customers/${customer.ID}`);
  };

  const removeRecent = (id, e) => {
    e.stopPropagation();
    const next = loadRecent().filter(c => c.ID !== id);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    setRecentlyViewed(next);
  };

  const handleDeleteCustomer = async (customer, e) => {
    e.stopPropagation();
    if (deletingId) return;
    if (!window.confirm(`Delete ${customerName(customer)} (ID: ${customer.ID})? This only works if the account has no orders or appointments, and cannot be undone.`)) return;

    setDeletingId(customer.ID);
    try {
      await axios.delete(`${API_URL}/api/customers/${customer.ID}`);
      setCustomers(prev => {
        const next = prev.filter(c => c.ID !== customer.ID);
        setDuplicateIds(buildDuplicateSet(next));
        return next;
      });
      const nextRecent = loadRecent().filter(c => c.ID !== customer.ID);
      localStorage.setItem(RECENT_KEY, JSON.stringify(nextRecent));
      setRecentlyViewed(nextRecent);
      toast.success(`${customerName(customer)} deleted.`);
    } catch (err) {
      if (err.response?.status === 409) {
        const { orders = 0, appointments = 0 } = err.response.data || {};
        const parts = [];
        if (orders) parts.push(`${orders} order${orders !== 1 ? 's' : ''}`);
        if (appointments) parts.push(`${appointments} appointment${appointments !== 1 ? 's' : ''}`);
        toast.error(`Can't delete — account isn't empty (${parts.join(', ')}).`);
      } else {
        toast.error(err.response?.data?.error || 'Failed to delete customer.');
      }
    } finally {
      setDeletingId(null);
    }
  };

  // ── modal open/close ─────────────────────────────────────────────────────

  const openMassModal = () => {
    const isActiveJob = jobProgress && ['running', 'queued', 'resuming'].includes(jobProgress.status);
    // Only reset compose state when there's no active server-side job
    if (!isActiveJob) {
      setJobId(null);
      setJobProgress(null);
      setSendResult(null);
      setMassMessage('');
      setSelectedRecipients(new Map());
      setSendToAll(false);
      setAllCount(null);
    }
    setModalSearchTerm('');
    setModalSearchResults([]);
    setLocFilter({ state: '', city: '' });
    setLocCustomers([]);
    setShowMassModal(true);
    // Only focus compose input when not in progress view
    if (!isActiveJob) setTimeout(() => modalInputRef.current?.focus(), 50);
  };

  const closeMassModal = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setShowMassModal(false);
    setMassImage(null);
    setMassImagePreview(null);
  };

  // ── modal search ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!showMassModal) return;
    const id = setTimeout(async () => {
      if (modalSearchTerm.length >= 2) {
        setModalSearchLoading(true);
        try {
          const { data } = await axios.get(`${API_URL}/api/customers/search`, { params: { term: modalSearchTerm } });
          setModalSearchResults(data || []);
        } catch {
          setModalSearchResults([]);
        } finally {
          setModalSearchLoading(false);
        }
      } else {
        setModalSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [modalSearchTerm, showMassModal]);

  // ── location filter (debounced text inputs) ──────────────────────────────

  const handleLocFilter = (field, value) => {
    setLocFilter(prev => ({ ...prev, [field]: value }));
  };

  const modalOpenRef = useRef(false);
  useEffect(() => { modalOpenRef.current = showMassModal; }, [showMassModal]);

  useEffect(() => {
    const { state, city } = locFilter;
    const stateVal = state.trim();
    const cityVal  = city.trim();
    if (!modalOpenRef.current || (stateVal.length < 2 && cityVal.length < 2)) {
      setLocCustomers([]);
      return;
    }
    const id = setTimeout(async () => {
      setLoadingLoc(true);
      try {
        const { data } = await axios.get(`${API_URL}/api/customers/by-location`, {
          params: { state: stateVal, city: cityVal }
        });
        setLocCustomers(data || []);
      } catch { setLocCustomers([]); }
      finally { setLoadingLoc(false); }
    }, 400);
    return () => clearTimeout(id);
  }, [locFilter]);

  // ── recipient selection ──────────────────────────────────────────────────

  const toggleRecipient = (customer) => {
    setSelectedRecipients(prev => {
      const next = new Map(prev);
      next.has(customer.ID) ? next.delete(customer.ID) : next.set(customer.ID, customer);
      return next;
    });
  };

  const removeRecipient = (id) => {
    setSelectedRecipients(prev => { const next = new Map(prev); next.delete(id); return next; });
  };

  // Add all visible list items
  const addAll = (list) => {
    setSelectedRecipients(prev => {
      const next = new Map(prev);
      list.forEach(c => next.set(c.ID, c));
      return next;
    });
  };

  const clearAll = () => setSelectedRecipients(new Map());

  const addAllCustomers = async () => {
    if (fetchingCount) return;
    setFetchingCount(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/customers/mass-sms/eligible-count`);
      setAllCount(data.count);
      setSendToAll(true);
      setSelectedRecipients(new Map());
    } catch (err) {
      console.error('Failed to fetch eligible count:', err);
    } finally {
      setFetchingCount(false);
    }
  };

  const removeAllCustomers = () => {
    setSendToAll(false);
    setAllCount(null);
  };

  // ── send ─────────────────────────────────────────────────────────────────

  const sendMassText = async () => {
    if (!massMessage.trim() || (!selectedRecipients.size && !sendToAll)) return;
    setIsSending(true);
    setSendResult(null);
    setJobProgress(null);

    try {
      let finalMessage = massMessage.trim();

      if (massImage) {
        const formData = new FormData();
        formData.append('media', massImage);
        const { data: uploadData } = await axios.post(`${API_URL}/api/appointments/upload-media`, formData);
        finalMessage = `${finalMessage}\n\n${uploadData.url}`;
      }

      const audienceSummary = sendToAll
        ? 'All customers'
        : locFilter.city.trim() || locFilter.state.trim()
          ? `Location: ${[locFilter.city.trim(), locFilter.state.trim()].filter(Boolean).join(', ')}`
          : `${selectedRecipients.size} selected customer${selectedRecipients.size !== 1 ? 's' : ''}`;

      const { data } = await axios.post(`${API_URL}/api/customers/mass-sms/start`,
        sendToAll
          ? { sendToAll: true, message: finalMessage, audienceSummary }
          : { customerIds: [...selectedRecipients.keys()], message: finalMessage, audienceSummary }
      );

      const id = data.jobId;
      setJobId(id);
      const total = sendToAll ? (allCount || 0) : selectedRecipients.size;
      setJobProgress({ sent: 0, failed: 0, skipped: 0, total, status: 'queued' });

      pollRef.current = setInterval(async () => {
        if (isPollingFetchRef.current) return;
        isPollingFetchRef.current = true;
        try {
          const { data: status } = await axios.get(`${API_URL}/api/customers/mass-sms/status/${id}`);
          setJobProgress(status);

          if (['completed', 'cancelled', 'failed'].includes(status.status)) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setIsSending(false);
            setSendResult(status);
          }
        } catch (pollErr) {
          console.error('Failed to poll job status:', pollErr);
        } finally {
          isPollingFetchRef.current = false;
        }
      }, 2000);

    } catch (err) {
      setIsSending(false);
      const msg = err.response?.data?.error || err.message;
      setSendResult({ sent: 0, failed: selectedRecipients.size, status: 'failed', error: msg });
    }
  };

  const cancelMassJob = async () => {
    if (!jobId) return;
    try {
      await axios.post(`${API_URL}/api/customers/mass-sms/cancel/${jobId}`);
    } catch (err) {
      console.error('Failed to cancel job:', err);
    }
  };

  // ── derived ──────────────────────────────────────────────────────────────

  // What shows in the modal results pane
  const hasLocFilter = locFilter.state.trim().length >= 2 || locFilter.city.trim().length >= 2;
  const modalList = hasLocFilter ? locCustomers : (modalSearchTerm.length >= 2 ? modalSearchResults : []);
  const dupCount  = duplicateIds.size;

  // ── render ───────────────────────────────────────────────────────────────

  return (
    <div className="customer-search-container">

      {/* ── Page header ── */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title"><i className="fas fa-users" /> Customer Directory</h1>
            <p className="page-subtitle">Search and manage customer profiles, order history, and account details</p>
          </div>
        </div>
      </div>

      {/* ── Search bar ── */}
      <div className="search-section">
        <div className="search-bar-row">
          <div className="search-input-wrapper">
            <div className="search-icon"><i className="fas fa-search" /></div>
            <input
              ref={inputRef}
              type="text"
              className="search-input"
              placeholder="Search by name or phone number..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm('')} type="button">
                <i className="fas fa-times" />
              </button>
            )}
          </div>

          <button
            className="btn-mass-text-history"
            onClick={() => navigate('/customers/duplicates')}
            title="Find and clean up duplicate customer records"
          >
            <i className="fas fa-clone" /> Find Duplicates
          </button>

          {isAdmin && (
            <>
              <button
                className={`btn-mass-text${isSending ? ' btn-mass-text--active' : ''}`}
                onClick={openMassModal}
                title={isSending ? 'Mass text in progress — click to see progress' : 'Send mass text message'}
              >
                {isSending
                  ? <><span className="mass-text-pulse" /><i className="fas fa-sms" /> Sending…</>
                  : <><i className="fas fa-sms" /> Mass Text</>}
              </button>
              {getUserData()?.UserName?.toLowerCase() === 'noga' && (
                <button
                  className="btn-mass-text-history"
                  onClick={() => setShowHistory(true)}
                  title="View campaign history"
                >
                  <i className="fas fa-chart-bar" /> History
                </button>
              )}
            </>
          )}
        </div>

        {searchTerm.length > 0 && searchTerm.length < 2 && (
          <div className="search-message info"><i className="fas fa-info-circle" /><span>Type at least 2 characters to search</span></div>
        )}
        {isLoading && (
          <div className="search-message loading"><div className="loading-spinner" /><span>Searching customers...</span></div>
        )}
        {error && (
          <div className="search-message error"><i className="fas fa-exclamation-triangle" /><span>{error}</span></div>
        )}
      </div>

      {/* ── Main content ── */}
      <div className="content-section">

        {recentlyViewed.length > 0 && (
          <div className="recently-viewed">
            <div className="recent-chips">
              {recentlyViewed.map(c => (
                <div key={c.ID} className="recent-chip" onClick={() => handleCustomerClick(c)}>
                  <span>{customerName(c)}</span>
                  <button className="recent-chip-remove" onClick={(e) => removeRecent(c.ID, e)} title="Remove">
                    <i className="fas fa-times" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {searchTerm.length === 0 ? (
          <div className="welcome-state">
            <div className="welcome-content">
              <div className="welcome-icon"><i className="fas fa-search" /></div>
              <h2>Find Customer Profiles</h2>
              <p>Search above to view customer details, order history, and account information</p>
              <div className="search-tips">
                <h3>Search Tips:</h3>
                <div className="tips-grid">
                  <div className="tip-item"><i className="fas fa-user" /><span>Search by first name, last name, or full name</span></div>
                  <div className="tip-item"><i className="fas fa-phone" /><span>Search by phone number (with or without formatting)</span></div>
                  <div className="tip-item"><i className="fas fa-keyboard" /><span>Use ↑ ↓ arrow keys to navigate results, Enter to open</span></div>
                </div>
              </div>
            </div>
          </div>

        ) : searchTerm.length >= 2 && !isLoading && (
          <div className="results-section">
            {customers.length === 0 ? (
              <div className="no-results">
                <div className="no-results-icon"><i className="fas fa-user-slash" /></div>
                <h2>No customers found</h2>
                <p>No customers match "<strong>{searchTerm}</strong>"</p>
                <p className="suggestion">Try a different name or phone number</p>
              </div>
            ) : (
              <>
                <div className="results-header">
                  <h2>
                    <i className="fas fa-list" />
                    Search Results
                    <span className="results-count">({customers.length} found)</span>
                    {dupCount > 0 && (
                      <span className="duplicate-warning-badge">
                        <i className="fas fa-exclamation-triangle" /> {dupCount} possible duplicate{dupCount > 1 ? 's' : ''}
                      </span>
                    )}
                  </h2>
                </div>

                <div className="customer-table-container">
                  <div className="customer-table">
                    <div className="table-header">
                      <div className="header-cell name">Customer</div>
                      <div className="header-cell contact">Contact</div>
                      <div className="header-cell location">Location</div>
                      <div className="header-cell mold">Mold ID</div>
                      <div className="header-cell actions" style={{ width: '48px' }}></div>
                    </div>
                    <div className="table-body">
                      {customers.map((customer, idx) => {
                        const isDup = duplicateIds.has(customer.ID);
                        const isFoc = focusedIndex === idx;
                        return (
                          <div
                            key={customer.ID}
                            ref={el => rowRefs.current[idx] = el}
                            className={['customer-row', isFoc ? 'keyboard-focused' : '', isDup ? 'has-duplicate' : ''].filter(Boolean).join(' ')}
                            onClick={() => handleCustomerClick(customer)}
                          >
                            <div className="cell name">
                              <div className="customer-avatar"><i className="fas fa-user" /></div>
                              <div className="customer-name">
                                <div className="full-name">
                                  {highlightMatch(customerName(customer), searchTerm)}
                                  {isDup && <span className="duplicate-inline-badge" title="Possible duplicate"><i className="fas fa-clone" /></span>}
                                </div>
                                <div className="customer-id">ID: {customer.ID}</div>
                              </div>
                            </div>
                            <div className="cell contact" data-label="Contact">
                              <div className="contact-info">
                                <div className="phone"><i className="fas fa-phone" />{highlightMatch(formatPhone(customer.CustomerPhone), searchTerm)}</div>
                                {customer.CustomerEmail && <div className="email"><i className="fas fa-envelope" />{customer.CustomerEmail}</div>}
                              </div>
                            </div>
                            <div className="cell location" data-label="Location">
                              {customer.CustomerCity && customer.CustomerState
                                ? <div className="location-info"><i className="fas fa-map-marker-alt" />{customer.CustomerCity}, {customer.CustomerState}</div>
                                : <span className="no-data">N/A</span>}
                            </div>
                            <div className="cell mold" data-label="Mold ID">
                              {customer.MoldId
                                ? <div className="mold-id"><i className="fas fa-tag" />{customer.MoldId}</div>
                                : <span className="no-data">N/A</span>}
                            </div>
                            <div className="cell actions" style={{ width: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <button
                                className="btn-delete-customer"
                                onClick={(e) => handleDeleteCustomer(customer, e)}
                                disabled={deletingId === customer.ID}
                                title="Delete customer (only works if the account has no orders or appointments)"
                                style={{ background: 'none', border: 'none', color: '#dc3545', cursor: deletingId === customer.ID ? 'default' : 'pointer', opacity: deletingId === customer.ID ? 0.5 : 1, fontSize: '15px' }}
                              >
                                {deletingId === customer.ID
                                  ? <i className="fas fa-spinner fa-spin" />
                                  : <i className="fas fa-trash-alt" />}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {showHistory && <MassSMSHistory onClose={() => setShowHistory(false)} />}

      {/* ── Mass text modal ── */}
      {showMassModal && (
        <div className="mass-text-backdrop">
          <div className="mass-text-modal" onClick={e => e.stopPropagation()}>

            {sendResult ? (
              <div className="modal-body">
                <div className="send-result">
                  <div className={`send-result-icon ${sendResult.status === 'cancelled' ? 'partial' : sendResult.failed === 0 ? 'success' : 'partial'}`}>
                    <i className={`fas fa-${sendResult.status === 'cancelled' ? 'ban' : sendResult.failed === 0 ? 'check-circle' : 'exclamation-circle'}`} />
                  </div>
                  <h3>{sendResult.status === 'cancelled' ? 'Sending Cancelled' : 'Sending Complete'}</h3>
                  <p><strong>{sendResult.sent}</strong> sent successfully</p>
                  {sendResult.failed > 0 && <p className="send-failed"><strong>{sendResult.failed}</strong> failed</p>}
                  {sendResult.skipped > 0 && <p className="send-skipped"><strong>{sendResult.skipped}</strong> skipped (opted out / invalid number)</p>}
                  {sendResult.error && <p className="send-failed" style={{fontSize:'0.8rem'}}>{sendResult.error}</p>}
                  <button className="btn-modal-close" onClick={closeMassModal}>Close</button>
                </div>
              </div>
            ) : jobProgress ? (
              <div className="modal-body">
                <div className="send-progress">
                  <div className="send-progress-header">
                    <i className="fas fa-paper-plane" />
                    <h3>Sending Messages&hellip;</h3>
                  </div>
                  <div className="progress-bar-track">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${jobProgress.total > 0 ? Math.round((jobProgress.sent / jobProgress.total) * 100) : 0}%` }}
                    />
                  </div>
                  <p className="progress-counts">
                    <strong>{jobProgress.sent.toLocaleString()}</strong> / {jobProgress.total.toLocaleString()} sent
                    {jobProgress.failed > 0 && <span className="prog-failed"> &bull; {jobProgress.failed.toLocaleString()} failed</span>}
                    {jobProgress.skipped > 0 && <span className="prog-skipped"> &bull; {jobProgress.skipped.toLocaleString()} skipped</span>}
                  </p>
                  <p className="progress-eta">
                    {jobProgress.total > 0 && jobProgress.sent < jobProgress.total
                      ? `~${Math.ceil((jobProgress.total - jobProgress.sent) / 60)} min remaining`
                      : jobProgress.status === 'queued' ? 'Starting…' : ''}
                  </p>
                  {jobProgress.startedBy
                    ? <p className="progress-note">Started by <strong>{jobProgress.startedBy}</strong> — running on the server, safe to close this window.</p>
                    : <p className="progress-note">Running on the server — safe to close this window.</p>
                  }
                  <button className="btn-cancel-job" onClick={cancelMassJob}>
                    <i className="fas fa-stop-circle" /> Stop Sending
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="modal-header">
                  <h3><i className="fas fa-sms" /> Mass Text Message</h3>
                  <button className="btn-modal-close-x" onClick={closeMassModal}><i className="fas fa-times" /></button>
                </div>

                <div className="modal-body">
                  {/* ── Find customers section ── */}
                  <div className="modal-find-section">
                    <div className="modal-find-label">
                      <span>Find Customers</span>
                      <button
                        className="btn-add-all-customers"
                        onClick={addAllCustomers}
                        disabled={fetchingCount || sendToAll}
                        title="Select all eligible customers for mass text"
                      >
                        {fetchingCount
                          ? <><div className="loading-spinner loading-spinner--sm" /> Loading…</>
                          : <><i className="fas fa-users" /> Add All Customers</>}
                      </button>
                    </div>

                    {/* Search bar */}
                    <div className="modal-search-wrapper">
                      <i className="fas fa-search modal-search-icon" />
                      <input
                        ref={modalInputRef}
                        type="text"
                        className="modal-search-input"
                        placeholder="Search by name or phone..."
                        value={modalSearchTerm}
                        onChange={e => setModalSearchTerm(e.target.value)}
                      />
                      {modalSearchTerm && (
                        <button className="modal-search-clear" onClick={() => setModalSearchTerm('')}>
                          <i className="fas fa-times" />
                        </button>
                      )}
                    </div>

                    {/* Location filters */}
                    <div className="modal-loc-filters">
                      <input
                        type="text"
                        className="modal-loc-input"
                        placeholder="State (e.g. NY)"
                        value={locFilter.state}
                        onChange={e => handleLocFilter('state', e.target.value)}
                      />
                      <input
                        type="text"
                        className="modal-loc-input"
                        placeholder="City (e.g. Brooklyn)"
                        value={locFilter.city}
                        onChange={e => handleLocFilter('city', e.target.value)}
                      />
                    </div>

                    {/* Results list */}
                    <div className="modal-results-pane">
                      {(modalSearchLoading || loadingLoc) && (
                        <div className="modal-results-loading"><div className="loading-spinner" /> Searching...</div>
                      )}

                      {!modalSearchLoading && !loadingLoc && modalList.length > 0 && (
                        <>
                          <div className="modal-results-header">
                            <span>{modalList.length} customer{modalList.length !== 1 ? 's' : ''} found</span>
                            <button className="btn-select-all-modal" onClick={() => addAll(modalList)}>Add all</button>
                          </div>
                          <div className="modal-results-list">
                            {modalList.map(c => {
                              const isSelected = selectedRecipients.has(c.ID);
                              return (
                                <label key={c.ID} className={`modal-result-row${isSelected ? ' is-selected' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleRecipient(c)}
                                  />
                                  <span className="recipient-name">{customerName(c)}</span>
                                  <span className="recipient-phone">{formatPhone(c.CustomerPhone)}</span>
                                  <span className="recipient-loc">{[c.CustomerCity, c.CustomerState].filter(Boolean).join(', ')}</span>
                                </label>
                              );
                            })}
                          </div>
                        </>
                      )}

                      {!modalSearchLoading && !loadingLoc && modalList.length === 0 && (
                        <div className="modal-results-empty">
                          {hasLocFilter
                            ? 'No customers found for selected location'
                            : modalSearchTerm.length >= 2
                              ? 'No customers match that search'
                              : 'Search by name/phone or filter by location above'}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── Selected recipients ── */}
                  <div className="modal-selected-section">
                    <div className="modal-selected-header">
                      <span className="modal-selected-label">
                        <i className="fas fa-user-check" /> Recipients
                        <strong className="modal-selected-count">
                          {sendToAll ? (allCount?.toLocaleString() ?? '…') : selectedRecipients.size}
                        </strong>
                      </span>
                      {(sendToAll || selectedRecipients.size > 0) && (
                        <button className="btn-clear-recipients" onClick={sendToAll ? removeAllCustomers : clearAll}>
                          Clear all
                        </button>
                      )}
                    </div>
                    {sendToAll ? (
                      <div className="modal-selected-chips">
                        <div className="selected-chip selected-chip--all">
                          <i className="fas fa-users" />
                          <span>All Eligible Customers{allCount ? ` (~${allCount.toLocaleString()})` : ''}</span>
                          <button onClick={removeAllCustomers} title="Remove"><i className="fas fa-times" /></button>
                        </div>
                      </div>
                    ) : selectedRecipients.size > 0 ? (
                      <div className="modal-selected-chips">
                        {[...selectedRecipients.values()].map(c => (
                          <div key={c.ID} className="selected-chip">
                            <span>{customerName(c)}</span>
                            <button onClick={() => removeRecipient(c.ID)}><i className="fas fa-times" /></button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="modal-selected-empty">No recipients selected yet</div>
                    )}
                  </div>

                  {/* ── Message ── */}
                  <div className="modal-message-area">
                    <label>Message</label>
                    <textarea
                      value={massMessage}
                      onChange={e => setMassMessage(e.target.value)}
                      placeholder="Type your message here..."
                      rows={5}
                      maxLength={1000}
                    />
                    <div className="char-count">{massMessage.length} / 1000</div>

                    {/* Image attachment */}
                    <input
                      ref={massImageInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setMassImage(file);
                        setMassImagePreview(URL.createObjectURL(file));
                        e.target.value = '';
                      }}
                    />

                    {massImagePreview ? (
                      <div className="mass-image-preview">
                        <img src={massImagePreview} alt="attachment preview" />
                        <button
                          className="mass-image-remove"
                          onClick={() => { setMassImage(null); setMassImagePreview(null); }}
                          title="Remove image"
                        >
                          <i className="fas fa-times" />
                        </button>
                        <span className="mass-image-name">{massImage?.name}</span>
                      </div>
                    ) : (
                      <button
                        className="mass-attach-btn"
                        onClick={() => massImageInputRef.current?.click()}
                        type="button"
                      >
                        <i className="fas fa-image" /> Attach Image
                      </button>
                    )}
                  </div>

                  <div className="modal-actions">
                    <button className="btn-modal-cancel" onClick={closeMassModal}>Cancel</button>
                    <button
                      className="btn-send-sms"
                      onClick={sendMassText}
                      disabled={isSending || !massMessage.trim() || (!selectedRecipients.size && !sendToAll)}
                    >
                      {isSending
                        ? <><div className="loading-spinner" /> Sending...</>
                        : <><i className="fas fa-paper-plane" /> Send to {sendToAll ? `~${allCount?.toLocaleString() ?? 'All'}` : selectedRecipients.size}</>}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSearch;
